import { useCallback } from "react";
import { useQuery } from "react-query";
import {
  GET_CLINICAL_PROGRAM_TARGETS,
  GET_LIST_OF_GOALS,
  STALE_TIME_5_MINUTES,
} from "@constants/reactQueryKeys";
import { getClinicalProgramTargets } from "api/clinicalManagement";
import { getListOfGoals } from "api/goals";
import type { Target, Goal } from "api/types/aba";
import type { ClinicalTarget } from "@answersnow/pkg-clinical-program";
import extractRecords from "../utils/extractRecords";

interface PromotedTargetRecord {
  id: number;
  client_id: number;
  target_id: number;
  rank: number;
  promoted_at: string;
  target: Target;
}

const ELIGIBLE_STATUSES = new Set(["in-progress", "met"]);

const GOALS_QUERY_PARAMS = {} as const;

const toClinicalTarget = (target: Target, goalTitle: string): ClinicalTarget => ({
  id: String(target.id),
  name: target.title,
  goalName: goalTitle,
  phase: target.phase,
  status: target.status,
  lastUpdated: target.updated_at
    ? new Date(target.updated_at).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })
    : undefined,
});

const uniqueByTargetId = (records: PromotedTargetRecord[]): PromotedTargetRecord[] => {
  const byId = new Map<number, PromotedTargetRecord>();
  for (const record of records) {
    if (!byId.has(record.target_id)) {
      byId.set(record.target_id, record);
    }
  }
  return [...byId.values()];
};

const useGetClinicalProgramTargets = (clientId: number | null) => {
  const enabled = Boolean(clientId);
  const safeClientId = clientId ?? 0;

  const promotedQuery = useQuery(
    [GET_CLINICAL_PROGRAM_TARGETS, clientId],
    () => getClinicalProgramTargets(safeClientId),
    {
      staleTime: STALE_TIME_5_MINUTES,
      enabled,
      refetchOnWindowFocus: false,
      refetchOnMount: "always",
    }
  );

  const goalsQuery = useQuery(
    [GET_LIST_OF_GOALS, String(safeClientId), GOALS_QUERY_PARAMS],
    () => getListOfGoals(String(safeClientId), GOALS_QUERY_PARAMS),
    {
      staleTime: STALE_TIME_5_MINUTES,
      enabled,
      refetchOnWindowFocus: false,
      refetchOnMount: "always",
    }
  );

  const isLoading = promotedQuery.isLoading || goalsQuery.isLoading;
  const isError = promotedQuery.isError || goalsQuery.isError;
  const isFetching = promotedQuery.isFetching || goalsQuery.isFetching;

  const promotedRecords = uniqueByTargetId(
    extractRecords<PromotedTargetRecord>(promotedQuery.data)
  );

  const goals = extractRecords<Goal>(goalsQuery.data);

  // Build a goal title lookup from all goal targets
  const goalTitleById = new Map<number, string>();
  for (const goal of goals) {
    for (const target of goal.targets ?? []) {
      goalTitleById.set(target.id, goal.title);
    }
  }

  // Promoted targets ordered by rank
  const selectedTargets: ClinicalTarget[] = [...promotedRecords]
    .sort((a, b) => a.rank - b.rank)
    .map((record) => toClinicalTarget(record.target, goalTitleById.get(record.target_id) ?? ""));

  const promotedIds = new Set(promotedRecords.map((r) => String(r.target_id)));

  // All non-archived, non-promoted targets with eligible statuses
  const availableTargets: ClinicalTarget[] = goals.flatMap((goal) =>
    (goal.targets ?? [])
      .filter(
        (t) => !t.is_archived && !promotedIds.has(String(t.id)) && ELIGIBLE_STATUSES.has(t.status)
      )
      .map((t) => toClinicalTarget(t, goal.title))
  );

  const allTargets = goals.flatMap((g) => g.targets ?? []).filter((t) => !t.is_archived);
  const settled = !isLoading && !isError;

  // No targets created on the program at all
  const hasNoTargets = settled && allTargets.length === 0;

  // Targets exist but none are in an eligible status and none are promoted
  const hasIneligibleTargets =
    settled &&
    allTargets.length > 0 &&
    availableTargets.length === 0 &&
    selectedTargets.length === 0;

  const refetch = useCallback(
    () => Promise.all([promotedQuery.refetch(), goalsQuery.refetch()]),
    [promotedQuery.refetch, goalsQuery.refetch]
  );

  return {
    selectedTargets,
    availableTargets,
    hasNoTargets,
    hasIneligibleTargets,
    isLoading,
    isError,
    isFetching,
    refetch,
  };
};

export default useGetClinicalProgramTargets;
